import { Box, Button, Divider, Switch } from "@mui/material";
import Grid from "@mui/material/Grid";
import DialogModal from "../DialogModal/DialogModal";
import React, { useState } from "react";
import { useCookieConsent } from "../../provider/CookieConsent/CookieConsentProvider";
import Typography from "@mui/material/Typography";
import cookieBannerStyle from "@/src/components/CookieBanner/cookieBannerStyle";
import { mergeSx } from "@/src/utils/mergeSx";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";

type CookieModalProps = {
  open: boolean;
  setIsConfigureOpen: (open: boolean) => void;
  isSelectionMade: boolean;
  setIsSelectionMade: (isSelectionMade: boolean) => void;
};

const CookieModal = ({ open, setIsConfigureOpen, isSelectionMade, setIsSelectionMade }: CookieModalProps) => {
  const { saveConsent, hasConsent } = useCookieConsent();
  const [analyticsChecked, setAnalyticsChecked] = useState(hasConsent("analytics"));

  const saveSelection = () => {
    saveConsent({
      consentType: "configured",
      config: {
        analytics: analyticsChecked,
      },
    });
  };

  return (
    <DialogModal
      open={open && !isSelectionMade}
      onClose={() => setIsConfigureOpen(false)}
      title={"Cookie-Einstellungen anpassen"}
      maxWidth={"lg"}
      responsive={true}
      variant={"clean"}
      actions={<ModalActions setAnalyticsChecked={setAnalyticsChecked} setIsSelectionMade={setIsSelectionMade} saveSelection={saveSelection} />}
    >
      <Box>
        <Divider sx={cookieBannerStyle.divider} />
        <Typography variant={"body2"}>Wir verwenden Cookies und ähnliche Tools (zusammen "Cookies") für folgende Zwecke:</Typography>
        <Typography variant={"body2"} sx={mergeSx(cookieBannerStyle.headline, { marginTop: "1.5rem" })}>
          <strong>Essenziell</strong>
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Grid size={{ xs: 10, sm: 11 }}>
            <Typography variant={"body2"}>
              Diese Cookies sind erforderlich, um unsere Website und Services bereitzustellen und können nicht deaktiviert werden. Sie werden in der Regel als
              Reaktion auf Ihre Aktionen auf der Website festgelegt, z. B. die Festlegung Ihrer Datenschutzeinstellungen, die Anmeldung oder das Ausfüllen von
              Formularen sowie den Bezahlprozess.
            </Typography>
          </Grid>
          <Grid size={{ xs: 2, sm: 1 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Switch color="primary" defaultChecked disabled />
          </Grid>
        </Grid>

        <Divider sx={cookieBannerStyle.divider} />
        <Typography variant={"body2"} sx={cookieBannerStyle.headline}>
          <strong>Analyse & Funktionen</strong>
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Grid size={{ xs: 10, sm: 11 }}>
            <Typography variant={"body2"}>
              Technologien dieser Kategorie ermöglichen es uns, die Nutzung der Website zu analysieren, um deren Leistung zu messen und zu verbessern. Zudem
              werden sie verwendet, um erweiterte Funktionen und Personalisierungen bereitzustellen, die das Nutzererlebnis optimieren und eine hohe
              Datenqualität bei der Interaktion mit unseren Diensten sicherstellen.
            </Typography>
          </Grid>
          <Grid size={{ xs: 2, sm: 1 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Switch color="primary" checked={analyticsChecked} onChange={({ target }) => setAnalyticsChecked(target.checked)} />
          </Grid>
        </Grid>

        <Divider sx={cookieBannerStyle.divider} />
        <Typography variant={"body2"}>
          Das Blockieren einiger Arten von Cookies kann sich auf Ihre Erfahrung auf unseren Websites auswirken. Um mehr über die auf unserer Homepage genutzten
          Cookies zu erfahren und Ihre Cookie-Einstellungen jederzeit zu ändern besuchen Sie bitte unsere{" "}
          <LinkLegalbird to={"/cookierichtlinie"} standalone onClick={() => setIsConfigureOpen(false)}>
            Cookierichtlinie
          </LinkLegalbird>
          .
        </Typography>
        <Divider sx={cookieBannerStyle.divider} />
      </Box>
    </DialogModal>
  );
};

const ModalActions = ({
  setIsSelectionMade,
  saveSelection,
  setAnalyticsChecked = () => {},
}: {
  setIsSelectionMade: (isSelectionMade: boolean) => void;
  saveSelection: Function;
  setAnalyticsChecked: (analyticsChecked: boolean) => void;
}) => {
  const { denyAll, allowAll } = useCookieConsent();

  return (
    <Grid container spacing={2} sx={{ width: "100%", justifyContent: "flex-end" }}>
      <Grid
        size={{
          md: "auto",
          xs: 12,
        }}
      >
        <Button
          fullWidth
          variant={"outlined"}
          size={"small"}
          onClick={() => {
            setIsSelectionMade(true);
            setAnalyticsChecked(false);
            denyAll();
          }}
        >
          Ablehnen
        </Button>
      </Grid>
      <Grid
        size={{
          md: "auto",
          xs: 12,
        }}
      >
        <Button
          fullWidth
          variant={"outlined"}
          size={"small"}
          onClick={() => {
            setIsSelectionMade(true);
            saveSelection();
          }}
        >
          Auswahl speichern
        </Button>
      </Grid>
      <Grid
        size={{
          md: "auto",
          xs: 12,
        }}
      >
        <Button
          fullWidth
          variant={"contained"}
          size={"small"}
          onClick={() => {
            setIsSelectionMade(true);
            setAnalyticsChecked(true);
            allowAll();
          }}
        >
          Alle akzeptieren
        </Button>
      </Grid>
    </Grid>
  );
};

export default CookieModal;
